import type { IconGlyph } from '@sunwebgroup/uds-ui/icon'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import { MenuItem, MenuLinkItem } from '@sunwebgroup/uds-ui/menu'
import { Popover } from '@sunwebgroup/uds-ui/popover'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { createAccountHeaderClickEvent } from '@/domain/header/analytics'
import { useUser } from '@/domain/user/hooks'
import { useIntl } from '@/intl'
import TopNavigationBarItem from '@/scopes/common/components/Header/TopNavigationBarItem'
import UserAvatar from '@/scopes/common/components/Header/UserAvatar'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useFavouriteProductIdsQuery } from '@/scopes/favourites/domain/hooks'
import styles from './AccountMenu.module.css'

interface MenuItemProps {
  ref?: React.Ref<HTMLAnchorElement>
  href?: string
  onClick?: () => void
  icon?: IconGlyph
  labelId: string
  rightText?: number
  onClose?: () => void
  isLoading?: boolean
}

function AccountMenuItem({
  ref,
  href,
  onClick,
  icon,
  labelId,
  rightText,
  onClose,
  isLoading,
}: MenuItemProps) {
  const intl = useIntl()

  const handleClick = () => {
    if (isLoading)
      return
    onClick?.()
    onClose?.()
  }

  return (
    <div className={styles.dropdownMenuItem}>
      <div className={styles.menuItemWrapper}>
        <div className={cn(styles.loadingSpinner, isLoading && styles.loadingSpinnerVisible)}>
          <IndeterminateProgressCircle size={20} />
        </div>
        {href
          ? (
              <MenuLinkItem
                glyph={icon}
                href={href}
                onClick={handleClick}
                rightSlot={rightText?.toString()}
                ref={ref}
              >
                {intl.formatMessage({ id: labelId })}
              </MenuLinkItem>
            )
          : (
              <MenuItem onClick={handleClick}>
                {intl.formatMessage({ id: labelId })}
              </MenuItem>
            )}
      </div>
    </div>
  )
}

const LoggedOutMenu: React.FC<{
  autoFocus: boolean
  onClose: () => void
}> = ({
  autoFocus,
  onClose,
}) => {
  const intl = useIntl()
  const links = useConfiguration('links')
  const { sendEvent } = useAnalytics()
  const { isSunwebCE, logout, isLoggingOut } = useUser()
  const { data: favourites } = useFavouriteProductIdsQuery()
  const track = (result: string, description: string) => () => {
    sendEvent(createAccountHeaderClickEvent({ result, description }))
  }
  const buttonRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    if (autoFocus) {
      buttonRef.current?.focus()
    }
  }, [autoFocus])

  const handleLogout = () => {
    logout?.(undefined, {
      onSuccess: () => {
        onClose()
      },
    })
  }

  return (
    <>
      <div className={styles.upperLogoutContainer}>
        <div className={styles.titleUpperLogoutWrapper}>
          <Typography variant="display-h-regular">
            {intl.formatMessage({ id: 'header_dropdown_menu_title' })}
          </Typography>
        </div>
        <Button
          as="a"
          className={styles.loginButton}
          href={`${links.login}?returnurl=${links.myPortal}`}
          fullWidth
          onClick={() => {
            track('Click on My Sunweb', 'redirects to the login page')()
            onClose()
          }}
          data-link-type="account-menu-login-button"
          ref={buttonRef}
        >
          {intl.formatMessage({ id: 'header_dropdown_menu_login' })}
        </Button>
        <Button
          as="a"
          href={links.createAccount}
          variant="secondary"
          fullWidth
          onClick={() => {
            track('Click on Create Account', 'redirects to the account creation page')()
            onClose()
          }}
        >
          {intl.formatMessage({ id: 'header_dropdown_menu_create_account' })}
        </Button>
      </div>

      <div className={styles.dropdownMenuDivider} />
      {!isSunwebCE && (
        <>
          <AccountMenuItem
            href={links.myPortal}
            icon="Suitcase"
            labelId="header_my_bookings"
            onClick={track('Click on My Bookings', 'redirects to \'My Sunweb\' page')}
            onClose={onClose}
          />
          <AccountMenuItem
            href={links.favourites}
            icon="Heart"
            labelId="header_my_favourites"
            rightText={favourites?.length ?? 0}
            onClick={track('Click on Favorites', 'redirects to the favorites page')}
            onClose={onClose}
          />
        </>
      )}
      {links.loginCE
      && (isSunwebCE
        ? (
            <AccountMenuItem
              onClick={handleLogout}
              labelId={isLoggingOut ? 'logging_out' : 'header_dropdown_menu_logout_ce'}
              onClose={undefined}
              isLoading={isLoggingOut}
            />
          )
        : (
            <AccountMenuItem
              href={links.loginCE}
              icon="TicketWithPercentage"
              labelId="header_login_ce"
              onClose={onClose}
            />
          ))}
    </>
  )
}

const LoggedInMenu: React.FC<{
  autoFocus: boolean
  onClose: () => void
}> = ({
  autoFocus,
  onClose,
}) => {
  const links = useConfiguration('links')
  const { data: favourites } = useFavouriteProductIdsQuery()
  const { logout, isLoggingOut } = useUser()
  const items: MenuItemProps[] = [
    { href: links.myPortal, icon: 'Suitcase', labelId: 'header_my_bookings' },
    { href: links.favourites, icon: 'Heart', labelId: 'header_my_favourites', rightText: favourites?.length },
  ]
  const buttonRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    if (autoFocus) {
      buttonRef.current?.focus()
    }
  }, [autoFocus])

  const handleLogout = () => {
    logout?.(undefined, {
      onSuccess: () => {
        onClose()
      },
    })
  }

  if (links.loginCE) {
    items.push({ href: links.loginCE, icon: 'TicketWithPercentage', labelId: 'header_login_ce' })
  }

  return (
    <>
      {items.map((item, i) => (
        <AccountMenuItem
          key={i}
          onClose={onClose}
          ref={i === 0 && autoFocus ? buttonRef : undefined}
          {...item}
        />
      ))}
      <div className={cn(styles.dropdownMenuDivider, styles.dropdownMenuDividerLogged)} />
      <AccountMenuItem href={links.myProfile} labelId="header_my_profile" onClose={onClose} />
      <div className={cn(styles.dropdownMenuDivider, styles.dropdownMenuDividerLogged)} />
      <AccountMenuItem
        onClick={handleLogout}
        labelId={isLoggingOut ? 'logging_out' : 'header_dropdown_menu_logout'}
        onClose={undefined}
        isLoading={isLoggingOut}
      />
    </>
  )
}

export function AccountMenu() {
  const intl = useIntl()
  const links = useConfiguration('links')
  const { capitalLetter, isLoggedIn, isSunwebCE } = useUser()
  const showLoggedIn = isLoggedIn && (!links.loginCE || !isSunwebCE)
  const [isAutoFocus, setIsAutoFocus] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const handleClose = () => {
    setIsOpen(false)
  }

  const TriggerIcon
    = isLoggedIn && capitalLetter
      ? (
          <UserAvatar />
        )
      : (
          <Icon glyph="PersonInCircle" />
        )

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger>
        <TopNavigationBarItem
          className={styles.trigger}
          aria-labelledby="account-menu-label"
          onClick={e => setIsAutoFocus(e.detail === 0)}
        >
          {TriggerIcon}
          <Typography id="account-menu-label" className={styles.triggerLabel}>{intl.formatMessage({ id: 'my_portal' })}</Typography>
        </TopNavigationBarItem>
      </Popover.Trigger>
      <Popover.Content className={styles.popoverContent} position="bottom-left" title={intl.formatMessage({ id: 'my_portal' })} closeText={intl.formatMessage({ id: 'close' })}>
        {showLoggedIn
          // eslint-disable-next-line jsx-a11y/no-autofocus -- custom prop forwarded to an internal control, not a native DOM autofocus attribute
          ? <LoggedInMenu autoFocus={isAutoFocus} onClose={handleClose} />
          // eslint-disable-next-line jsx-a11y/no-autofocus -- custom prop forwarded to an internal control, not a native DOM autofocus attribute
          : <LoggedOutMenu autoFocus={isAutoFocus} onClose={handleClose} />}
      </Popover.Content>
    </Popover.Root>
  )
}
